/**
 * PreviewIndexContext — optional preview overlay for Standard workspace rows.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { createContext, useCallback, useContext, useMemo, type FC, type ReactNode } from 'react';
import type {
  FlowLayer,
  FlowOptimizerItemPreview,
  TagTrafficProvider,
  TrackerId,
} from '@trafficpulse/shared';

import { FlowTrackerProvider } from '@/features/flow-optimizer/tracker/flow-tracker-context';

import { buildFlowPreviewRankLookups, type FlowPreviewRankLookups } from './flow-preview-ranks';
import { FlowTagsProvider } from './flow-tags-context';
import {
  buildPreviewIndex,
  previewKey,
  type PreviewIndex,
  type PreviewRow,
} from './preview-row.types';

const PreviewIndexContext = createContext<PreviewIndex | null>(null);
const PreviewRankContext = createContext<FlowPreviewRankLookups | null>(null);

export interface PreviewIndexProviderProps {
  items: ReadonlyArray<FlowOptimizerItemPreview>;
  /** Campaign's SMSWiser country, for per-country flow-tag thresholds. */
  countryKey?: string;
  /** Previewed campaign + tracker, for per-campaign flow-tag thresholds. */
  campaignId?: string;
  provider?: TagTrafficProvider;
  /** The tracker the rows belong to (hover CAP block, per-tracker rows).
   *  Absent = inherit the surrounding scope, or today's rendering. */
  tracker?: TrackerId;
  children: ReactNode;
}

export const PreviewIndexProvider: FC<PreviewIndexProviderProps> = ({
  items,
  countryKey,
  campaignId,
  provider,
  tracker,
  children,
}) => {
  const index = useMemo(() => buildPreviewIndex([...items]), [items]);
  const ranks = useMemo(
    () => (items.length > 0 ? buildFlowPreviewRankLookups(items) : null),
    [items],
  );
  return (
    <PreviewIndexContext.Provider value={index}>
      <PreviewRankContext.Provider value={ranks}>
        <FlowTagsProvider
          items={items}
          countryKey={countryKey}
          campaignId={campaignId}
          provider={provider}
        >
          {tracker ? <FlowTrackerProvider tracker={tracker}>{children}</FlowTrackerProvider> : children}
        </FlowTagsProvider>
      </PreviewRankContext.Provider>
    </PreviewIndexContext.Provider>
  );
};

export function usePreviewRow(
  layer: FlowLayer,
  itemId: string,
  parentId?: string,
): PreviewRow | null {
  const index = useContext(PreviewIndexContext);
  if (!index) return null;
  return index.get(previewKey(layer, itemId, parentId)) ?? null;
}

export function usePreviewMode(): boolean {
  return useContext(PreviewIndexContext) != null;
}

/**
 * Stable, non-hook lookup of a preview row — for sorting a list by a preview
 * metric in a parent (where `usePreviewRow` can't be called per item). Uses the
 * exact same index + key as `usePreviewRow`, so sort values match the rows.
 */
export function usePreviewRowLookup(): (
  layer: FlowLayer,
  itemId: string,
  parentId?: string,
) => PreviewRow | null {
  const index = useContext(PreviewIndexContext);
  return useCallback(
    (layer, itemId, parentId) =>
      index?.get(previewKey(layer, itemId, parentId)) ?? null,
    [index],
  );
}

export function usePreviewRankLookups(): FlowPreviewRankLookups | null {
  return useContext(PreviewRankContext);
}

export function usePreviewRank(
  layer: FlowLayer,
  itemId: string,
  pathId?: string,
): number | undefined {
  const lookups = useContext(PreviewRankContext);
  if (!lookups) return undefined;
  return lookups.get(layer, itemId, pathId);
}
